import { ChevronLeftIcon } from '@heroicons/react/outline';
import { ConfigProvider, Select, ThemeConfig, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import { useRecoilValue } from 'recoil';

import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import CoinHeader from '../../../../assets/Icons/NewHeaderIcons/CoinHeader';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { User } from '../../../utils/interfaces/User';

export interface CardData {
  id: number;
  coins: number;
  title: string;
  description: string;
  buttonText: string;
  claimable: boolean;
  createdAt: string;
  isUsed: boolean;
  isExpired: boolean;
}

interface Props {
  data: CardData[];
  handleClaimRewardDynamic: (id: string) => void;
  loading: { value: boolean; id: string | null };
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
  setDates: React.Dispatch<
    React.SetStateAction<{ start_date: string; end_date: string }>
  >;
}

const config: ThemeConfig = {
  token: { colorPrimary: '#282829', fontFamily: 'inherit' },
};

const RewardsCard: React.FC<Props> = ({
  data: cardData,
  handleClaimRewardDynamic,
  loading,
  selectedValue,
  setSelectedValue,
  setDates,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const screens = useBreakpoint();
  const userData: User | undefined = useRecoilValue(user);

  const options = [
    { value: 'last7Days', label: 'Last 7 Days' },
    { value: 'last30Days', label: 'Last 30 Days' },
    { value: 'last90Days', label: 'Last 3 Months' },
    { value: 'customRange', label: 'All Time' },
  ];

  const handleCoinClick = (id: string, title: string, coin: number) => {
    const eventParams: EventDetails = {
      event_name: 'collect_reward',
      source: 'global',
      source_type: 'rewards',
      source_id: id as string,
      source_name: title,
      sub_source: null,
      sub_source_id: null,
      unit: 'coins',
      value: coin,
      meta: {
        click_source: 'rewards_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const handleChange = (value: string) => {
    setSelectedValue(value);

    let start_date = '';
    const end_date = moment().format('YYYY-MM-DD');

    switch (value) {
      case 'last7Days':
        start_date = moment().subtract(7, 'days').format('YYYY-MM-DD');
        break;
      case 'last30Days':
        start_date = moment().subtract(30, 'days').format('YYYY-MM-DD');
        break;
      case 'last90Days':
        start_date = moment().subtract(90, 'days').format('YYYY-MM-DD');
        break;
      case 'customRange':
        start_date = '1970-01-01';
        break;
      default:
        start_date = moment().subtract(30, 'days').format('YYYY-MM-DD');
    }

    setDates({ start_date, end_date });
  };

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col max-w-5xl xl:max-w-[60vw] w-full'>
        <div className='pt-10'>
          <div
            onClick={() => router.push('/')}
            className='flex gap-x-2 items-center mb-6 cursor-pointer group'
          >
            <button className='z-10 w-8 h-8 p-2 rounded-full text-white flex justify-center items-center bg-cartBgColor'>
              <ChevronLeftIcon className='text-white w-4 h-4 xl:w-7 xl:h-7' />
            </button>
            Back To Home
          </div>
        </div>

        <div className='flex flex-col w-full  md:px-20  no-scrollbar md:justify-center md:mt-4'>
          <div className='flex justify-start items-start xxxs:justify-between flex-col xxxs:flex-row gap-y-2 xxxs:items-center'>
            <Typography.Title
              level={1}
              className='text-xl  md:ext-2xl text-white font-semibold w-fit'
            >
              Collect Rewards
            </Typography.Title>
            <span>
              <ConfigProvider theme={{ ...config }}>
                <Select
                  value={selectedValue}
                  size='large'
                  onChange={handleChange}
                  className='bg-bgColor text-sm time-range-select'
                  options={options}
                  popupMatchSelectWidth={false}
                />
              </ConfigProvider>
            </span>
          </div>
          <ShouldRender check={cardData.length}>
            <div
              ref={scrollContainerRef}
              className='flex flex-col gap-4 no-scrollbar md:justify-start mt-3 md:mt-6 w-full'
            >
              {cardData
                .filter((item) => item.title)
                .map((card) => {
                  return (
                    <div
                      key={card.id}
                      style={{
                        borderRadius: '20px',
                      }}
                      className='flex items-start text-center flex-col md:flex-row  gap-5 justify-between shadow-lg p-5  text-white border border-dropDownMenuBg'
                    >
                      <div className='flex flex-col md:flex-row items-start text-center gap-x-5 gap-y-3 md:gap-y-5 h-full'>
                        <div className='flex flex-row md:flex-col items-center gap-x-1 md:gap-x-2 md:px-6 md:min-w-[90px] md:max-h-[100px] h-full justify-center px-3 py-2.5 md:py-3 rounded-2xl bg-[#151515]'>
                          <CoinHeader width={screens.md ? undefined : '24'} />
                          <span className='text-lg md:text-2xl font-bold md:mt-1'>
                            {card.coins}
                          </span>
                        </div>
                        <div className='flex flex-col  justify-center h-full w-full'>
                          <h3 className='text-base md:text-lg font-semibold text-start md:whitespace-nowrap'>
                            {card.title}
                          </h3>
                          <p className='text-xs md:text-sm text-start text-[#C7C6CF]'>
                            {card.description}
                          </p>
                          <span className='flex  justify-start mt-3 gap-1'>
                            <span className='border-dropDownMenuBg border rounded-full font-semibold text-xs px-4 py-1.5 whitespace-nowrap'>
                              <p className='text-[#504F53] inline-flex text-xs font-bold pr-1'>
                                Date :
                              </p>{' '}
                              {moment(card.createdAt).format('DD-MM-YYYY')}
                            </span>
                            <span className='border-dropDownMenuBg border rounded-full font-semibold text-xs px-4 py-1.5 whitespace-nowrap'>
                              <p className='text-[#504F53] inline-flex text-xs font-bold pr-1'>
                                Time :{' '}
                              </p>{' '}
                              {moment(card.createdAt).format('HH:mm:ss')}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className='flex justify-center items-center w-full md:w-fit h-full'>
                        <ButtonDefault
                          size={screens.md ? 4 : 2}
                          onClick={() => {
                            handleClaimRewardDynamic(card.id.toString());
                            handleCoinClick(
                              card.id.toString(),
                              card.title.toString(),
                              card.coins,
                            );
                          }}
                          rounded={false}
                          disabled={
                            card.isUsed ||
                            card.isExpired ||
                            (loading.id === card.id.toString() && loading.value)
                          }
                          roundedSm={true}
                          loading={
                            loading.id === card.id.toString() && loading.value
                          }
                          enableScaling={false}
                          variant={ButtonVariants.UNSTYLED}
                          className={clsx(
                            card.isExpired
                              ? 'disabled:bg-[#450A0A] text-[#EF4444]'
                              : 'disabled:bg-[#282829] text-black disabled:text-[#77777C]',
                            'w-full  py-4 font-medium disabled:cursor-not-allowed text-base bg-white  md:min-w-[142px] disabled:border-transparent ',
                          )}
                        >
                          {card.isUsed
                            ? 'Collected'
                            : card.isExpired
                              ? 'Expired'
                              : 'Collect'}
                        </ButtonDefault>
                      </div>
                    </div>
                  );
                })}
            </div>
          </ShouldRender>
          <ShouldRender check={!cardData.length}>
            <div className='flex justify-center items-center h-[60vh]'>
              <div className='flex flex-col justify-center gap-y-4 items-center '>
                <span className='text-lg'>No rewards to collect.</span>
                <span>
                  {' '}
                  <ButtonDefault
                    size={2}
                    rounded={false}
                    roundedSm={true}
                    onClick={() => router.push('/refer-earn')}
                    variant={ButtonVariants.WHITE}
                  >
                    <span className='flex text-base font-semibold gap-x-2 max-w-[174px] text-[#121212]  px-4 whitespace-nowrap'>
                      Start Earning
                    </span>
                  </ButtonDefault>
                </span>
              </div>
            </div>
          </ShouldRender>
        </div>
      </div>
    </div>
  );
};

export default RewardsCard;
